import { Phone, Instagram, Youtube, Facebook, MapPin, Heart } from "lucide-react";
import Monogram from "./Monogram";
import { Reveal } from "./Reveal";
import { STUDIO, NAV_LINKS } from "../data/siteContent";
import { scrollToId } from "../hooks/useLenis";

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-ink border-t border-gold/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-12">
        {/* Brand */}
        <div>
          <div className="flex items-center gap-3 mb-5">
            <Monogram className="w-10 h-10 text-gold" />
            <span className="leading-none">
              <span className="block font-display text-xl font-semibold tracking-[0.18em] text-ivory">
                MOHIT STUDIO
              </span>
              <span className="block text-[9px] uppercase tracking-[0.35em] text-gold mt-1.5">
                Wedding Photography & Films
              </span>
            </span>
          </div>
          <p className="font-display italic text-lg text-ivory-dim mb-5">
            “{STUDIO.taglineHi}”
          </p>
          <div className="flex items-center gap-3">
            <a
              data-testid="footer-instagram"
              href={STUDIO.instagram.url}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              className="w-10 h-10 border border-white/15 flex items-center justify-center text-ivory-dim hover:border-gold hover:text-gold transition-colors"
            >
              <Instagram className="w-4.5 h-4.5 w-5 h-5" />
            </a>
            <a
              data-testid="footer-youtube"
              href={STUDIO.instagram.url}
              target="_blank"
              rel="noreferrer"
              aria-label="YouTube"
              className="w-10 h-10 border border-white/15 flex items-center justify-center text-ivory-dim hover:border-gold hover:text-gold transition-colors"
            >
              <Youtube className="w-5 h-5" />
            </a>
            <a
              data-testid="footer-facebook"
              href={STUDIO.instagram.url}
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              className="w-10 h-10 border border-white/15 flex items-center justify-center text-ivory-dim hover:border-gold hover:text-gold transition-colors"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a
              data-testid="footer-whatsapp"
              href={STUDIO.whatsapp}
              target="_blank"
              rel="noreferrer"
              aria-label="WhatsApp"
              className="w-10 h-10 border border-white/15 flex items-center justify-center text-ivory-dim hover:border-[#25D366] hover:text-[#25D366] transition-colors"
            >
              <Phone className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Explore */}
        <div>
          <p className="eyebrow mb-6">Explore</p>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.id}>
                <a
                  href={`#${l.id}`}
                  data-testid={`footer-link-${l.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToId(l.id);
                  }}
                  className="text-sm text-ivory-dim hover:text-gold transition-colors"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <p className="eyebrow mb-6">Talk to Us</p>
          <ul className="space-y-4">
            {STUDIO.contacts.map((c) => (
              <li key={c.tel}>
                <a
                  href={c.tel}
                  data-testid={`footer-call-${c.tel.replace(/\D/g, "").slice(-4)}`}
                  className="group block"
                >
                  <span className="block text-xs text-ivory-mute mb-0.5">{c.name}</span>
                  <span className="flex items-center gap-2 font-display text-lg text-ivory group-hover:text-gold transition-colors">
                    <Phone className="w-4 h-4 text-gold" />
                    {c.phone}
                  </span>
                </a>
              </li>
            ))}
            <li>
              <a
                data-testid="footer-instagram-handle"
                href={STUDIO.instagram.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-sm text-ivory-dim hover:text-gold transition-colors"
              >
                <Instagram className="w-4 h-4 text-gold" />
                {STUDIO.instagram.handle}
              </a>
            </li>
          </ul>
        </div>

        {/* Find us / map placeholder */}
        <div>
          <p className="eyebrow mb-6">Find Us</p>
          <div
            data-testid="footer-map-placeholder"
            className="relative border border-gold/20 bg-ink-soft/60 aspect-[4/3] flex flex-col items-center justify-center text-center p-4 overflow-hidden"
          >
            <span className="absolute inset-0 opacity-[0.06] bg-[repeating-linear-gradient(0deg,transparent,transparent_19px,rgba(201,169,110,0.35)_19px,rgba(201,169,110,0.35)_20px),repeating-linear-gradient(90deg,transparent,transparent_19px,rgba(201,169,110,0.35)_19px,rgba(201,169,110,0.35)_20px)]" />
            <MapPin className="w-7 h-7 text-gold mb-3 relative" />
            <p className="font-display text-lg text-ivory relative">Keshod, Gujarat</p>
            <p className="text-[11px] text-ivory-mute relative mt-1 mb-3">
              Serving all of Saurashtra & destination weddings
            </p>
            <a
              data-testid="footer-maps-link"
              href="https://maps.google.com/?q=Keshod,+Gujarat"
              target="_blank"
              rel="noreferrer"
              className="relative text-[11px] uppercase tracking-[0.2em] text-gold border-b border-gold/40 pb-0.5 hover:text-gold-hover transition-colors"
            >
              Open in Google Maps
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-ivory-mute tracking-wide" data-testid="footer-copyright">
            © {year} {STUDIO.name}, {STUDIO.city}. All rights reserved.
          </p>
          <a
            href="/admin"
            data-testid="footer-studio-login"
            className="text-[11px] uppercase tracking-[0.2em] text-ivory-mute hover:text-gold transition-colors"
          >
            Studio Login
          </a>
          <p className="flex items-center gap-1.5 text-xs text-ivory-mute tracking-wide">
            Crafted with <Heart className="w-3 h-3 text-gold" fill="currentColor" strokeWidth={0} /> in Keshod
          </p>
        </div>
      </div>
    </footer>
  );
}
